import { formatCodeName, getFromMapSafely } from '../../../../common/utils';
import {
  DataObjectDimensionType,
  DataObjectFormDataType,
  DataObjectType,
  type DimensionFormDataType,
  type IndicatorFormDataType,
} from '../../../types';
import { AlgorithmType } from '../../schema/zAlgorithmType';
import { AlgorithmTargetMappingTargetOptionLabel } from '../AlgorithmTargetMappingTargetOptionLabel';
import { decodeRuleTargetItemValue, encodeRuleTargetItemValue } from '../utils';
import type {
  AlgorithmTargetMappingTargetOption,
  ResolvedSourceValue,
  ResolvedTargetValue,
  SourceDimensionsAndIndicatorsByCode,
  TargetDimensionsAndIndicatorsByCode,
} from './types';

const { SUBJECT, SUBJECT_ATTRIBUTE, CLASSIFIER } = DataObjectDimensionType;

export const resolveTargetValue = (
  currentTargetValue: string[],
  targetDimensionsAndIndicatorsByCode: TargetDimensionsAndIndicatorsByCode,
): ResolvedTargetValue => {
  const decodedTargetValue = decodeRuleTargetItemValue(currentTargetValue);
  const resolvedTargetValue: ResolvedTargetValue = {};

  for (const value of decodedTargetValue) {
    // If SUBJEKT dimension is selected, then not expecting any other values to be selected
    if ('dimensionCode' in value && value.dimensionCode === 'SUBJEKT') {
      return { isSubject: true, hasDimensions: true };
    }

    if ('dimensionCode' in value) {
      resolvedTargetValue.hasDimensions = true;

      const { dimensionType, classifier } = getFromMapSafely(
        targetDimensionsAndIndicatorsByCode,
        value.dimensionCode,
        (entity) => 'dimensionType' in entity,
      );

      if (dimensionType!.code === CLASSIFIER) {
        resolvedTargetValue.classifierId = classifier!.classifier.id;
      }

      if (dimensionType!.code === SUBJECT_ATTRIBUTE) {
        resolvedTargetValue.subjectAttributeCode = value.dimensionCode;
      }
    }

    if ('indicatorCode' in value && !value.confidentialityStatus) {
      resolvedTargetValue.hasIndicators = true;
    }

    if ('indicatorCode' in value && value.confidentialityStatus) {
      resolvedTargetValue.hasConfidentialityStatuses = true;
    }
  }

  return resolvedTargetValue;
};

export const getTargetOptions = ({
  algorithmTypeCode,
  dataObjectVersion,
  sourceValue,
  targetValue,
  targetDimensionsAndIndicators,
  sourceDimensionsAndIndicatorsByCode,
  confidentialityStatusCode,
}: {
  algorithmTypeCode: AlgorithmType;
  dataObjectVersion: DataObjectFormDataType;
  sourceValue: ResolvedSourceValue;
  targetValue: ResolvedTargetValue;
  targetDimensionsAndIndicators: Iterable<
    DimensionFormDataType | IndicatorFormDataType
  >;
  sourceDimensionsAndIndicatorsByCode: SourceDimensionsAndIndicatorsByCode;
  confidentialityStatusCode: string | undefined;
}) => {
  const targetOptions: AlgorithmTargetMappingTargetOption[] = [];

  const addTargetOption = (
    entity: { code: string; name: string },
    confidentialityStatus?: boolean,
  ) =>
    targetOptions.push({
      value: encodeRuleTargetItemValue(entity, confidentialityStatus),
      codeName: formatCodeName(entity),
      label: (
        <AlgorithmTargetMappingTargetOptionLabel
          {...entity}
          confidentialityStatus={confidentialityStatus}
        />
      ),
    });

  const excludeAllDimensions = !shouldIncludeDimensions(
    algorithmTypeCode,
    sourceValue,
    targetValue,
    sourceDimensionsAndIndicatorsByCode,
  );

  const excludeAllIndicators = !shouldIncludeIndicators(
    algorithmTypeCode,
    sourceValue,
    targetValue,
    sourceDimensionsAndIndicatorsByCode,
  );

  const excludeAllIndicatorConfidentialityStatuses =
    !shouldIncludeIndicatorConfidentialityStatuses(
      algorithmTypeCode,
      sourceValue,
      sourceDimensionsAndIndicatorsByCode,
      confidentialityStatusCode,
    );

  for (const entity of targetDimensionsAndIndicators) {
    const dimension = 'dimensionType' in entity ? entity : undefined;
    const indicator = 'dimensionType' in entity ? undefined : entity;

    // Only SUBJECT, SUBJECT_ATTRIBUTE and CLASSIFIER dimensions can be selected as target
    if (
      dimension &&
      dimension.dimensionType?.code !== SUBJECT &&
      dimension.dimensionType?.code !== SUBJECT_ATTRIBUTE &&
      dimension.dimensionType?.code !== CLASSIFIER
    ) {
      continue;
    }

    if (
      dataObjectVersion.dataObjectType === DataObjectType.IN &&
      algorithmTypeCode === AlgorithmType.INSERT &&
      entity.code === 'SUBJEKT'
    ) {
      continue;
    }

    // Other entities can not be selected alongside SUBJEKT dimensions
    if (targetValue.isSubject && entity.code !== 'SUBJEKT') {
      continue;
    }

    // SUBJEKT dimensions can only be targets of source SUBJEKT dimensions
    if (
      dimension &&
      sourceValue.dimensionOrIndicatorCode === 'SUBJEKT' &&
      dimension.dimensionType?.code !== SUBJECT
    ) {
      continue;
    }

    // If a classifier dimension is already selected, then
    // exclude dimensions that do not have the same classifier
    if (
      dimension &&
      targetValue.classifierId &&
      targetValue.classifierId !== dimension.classifier?.classifier.id
    ) {
      continue;
    }

    // If a classifier dimension is selected as source, then
    // exclude dimensions that do not have the same classifier
    if (
      dimension &&
      !sourceValue.isSubjectAttribute &&
      sourceValue.algorithmSourceId &&
      sourceValue.dimensionOrIndicatorCode &&
      isSourceValueOfType(
        'dimension',
        sourceValue,
        sourceDimensionsAndIndicatorsByCode,
      ) &&
      getFromMapSafely(
        getFromMapSafely(
          sourceDimensionsAndIndicatorsByCode,
          sourceValue.algorithmSourceId,
        ),
        sourceValue.dimensionOrIndicatorCode,
        (entity) => 'dimensionTypeCode' in entity,
      ).classifier?.id !== dimension.classifier?.classifier.id
    ) {
      continue;
    }

    // If a mapping is selected as source, then
    // exclude dimensions that do not have the same classifier as the source mapping's target classifier ("classifier 2")
    if (
      dimension &&
      sourceValue.classifierIdTo &&
      sourceValue.classifierIdTo !== dimension.classifier?.classifier.id
    ) {
      continue;
    }

    // If a SUBJECT_ATTRIBUTE dimension is already selected, then
    // exclude dimensions that are not of the same SUBJECT_ATTRIBUTE
    if (
      dimension &&
      targetValue.subjectAttributeCode &&
      targetValue.subjectAttributeCode !== dimension.code
    ) {
      continue;
    }

    // If a SUBJECT_ATTRIBUTE dimension is selected as source, then
    // exclude dimensions that are not of the same SUBJECT_ATTRIBUTE
    if (
      dimension &&
      sourceValue.isSubjectAttribute &&
      sourceValue.dimensionOrIndicatorCode !== dimension.code
    ) {
      continue;
    }

    if (dimension && !excludeAllDimensions) {
      addTargetOption(entity);
    }

    if (indicator && !excludeAllIndicators) {
      addTargetOption(entity, false);
    }

    if (indicator && !excludeAllIndicatorConfidentialityStatuses) {
      addTargetOption(entity, true);
    }
  }

  return targetOptions;
};

const shouldIncludeDimensions = (
  algorithmTypeCode: AlgorithmType,
  sourceValue: ResolvedSourceValue,
  targetValue: ResolvedTargetValue,
  sourceItemTypesByCode: SourceDimensionsAndIndicatorsByCode,
) => {
  if (algorithmTypeCode === AlgorithmType.UPDATE_INDICATOR) {
    return false;
  }

  if (targetValue.hasIndicators) {
    return false;
  }

  return !isSourceValueOfType('indicator', sourceValue, sourceItemTypesByCode);
};

const shouldIncludeIndicators = (
  algorithmTypeCode: AlgorithmType,
  sourceValue: ResolvedSourceValue,
  targetValue: ResolvedTargetValue,
  sourceItemTypesByCode: SourceDimensionsAndIndicatorsByCode,
) => {
  if (algorithmTypeCode === AlgorithmType.UPDATE_DIMENSION) {
    return false;
  }

  if (targetValue.hasDimensions) {
    return false;
  }

  if (targetValue.hasConfidentialityStatuses) {
    return false;
  }

  if (isSourceValueOfType('dimension', sourceValue, sourceItemTypesByCode)) {
    return false;
  }

  return !isSourceValueOfType('mapping', sourceValue, sourceItemTypesByCode);
};

const shouldIncludeIndicatorConfidentialityStatuses = (
  algorithmTypeCode: AlgorithmType,
  sourceValue: ResolvedSourceValue,
  sourceItemTypesByCode: SourceDimensionsAndIndicatorsByCode,
  confidentialityStatusCode: string | undefined,
) => {
  if (algorithmTypeCode !== AlgorithmType.INSERT) {
    return false;
  }

  if (!confidentialityStatusCode || confidentialityStatusCode === 'NONE') {
    return false;
  }

  return !isSourceValueOfType('indicator', sourceValue, sourceItemTypesByCode);
};

const isSourceValueOfType = (
  type: 'dimension' | 'indicator' | 'mapping',
  sourceValue: ResolvedSourceValue,
  sourceDimensionsAndIndicatorsByCode: SourceDimensionsAndIndicatorsByCode,
) => {
  if (type === 'mapping') {
    return Boolean(sourceValue.mappingId);
  }

  if (!sourceValue.algorithmSourceId || !sourceValue.dimensionOrIndicatorCode) {
    return false;
  }

  const entity = sourceDimensionsAndIndicatorsByCode
    .get(sourceValue.algorithmSourceId)
    ?.get(sourceValue.dimensionOrIndicatorCode);

  if (!entity) {
    return false;
  }

  if (type === 'dimension') {
    return 'dimensionTypeCode' in entity;
  }

  if (type === 'indicator') {
    return !('dimensionTypeCode' in entity);
  }

  throw new Error(
    `Invalid type. Expected "dimension", "indicator" or "mapping", but got: "${type}"`,
  );
};
